import { thenWait } from './promise';
import { setAttribute } from './dom';

const CONTROL_SELECTOR = '[data-form-control]';
const MESSAGE_SELECTOR = '[data-error-message]';

const FORM_ERROR_SELECTOR = '[data-form-error]';
const FORM_SUCCESS_SELECTOR = '[data-form-success]';

const MULTIPART_MIME_TYPE = 'multipart/form-data';
const JSON_MIME_TYPE = 'application/json';

const VALIDATION_OK = Symbol('validation:ok');

export type ValidationAction = (value: string, formData: Record<string, string>) => boolean;
export interface Validation {
    message: string|null;
    pattern?: RegExp;
    action?: ValidationAction;
}
export interface Validator {
    required?: boolean;
    message?: string|null;
    validations?: Validation[];
}
export type Validations = Record<string, Validator>;

/**
 * Serializes a form input data into an object with the input names mapped to their values
 */
export function formData<T extends object = Record<string, string>>($form: HTMLFormElement): T {
    const data: T = Object.create(null);
    const formData = new FormData($form);

    formData.forEach((field, key) => {
        const value = field instanceof File
            ? field.name
            : field;

        // @ts-ignore
        data[key] = value;
    });

    return data;
}

/**
 * Validates a form control
 */
function validateControl(formData: Record<string, string>, key: string, validator: Validator): string | null | (typeof VALIDATION_OK) {
    const { required, validations, message } = validator;
    const value = formData[key];

    // if value is required, early exit
    if (required && !value) {
        return message ?? null;
    }

    return validations?.find(validation => {
        const { pattern, action } = validation;

        // first test regex, then try custom function
        return pattern?.test(value) === false || action?.(value, formData) === false;
    })?.message ?? VALIDATION_OK;
}

/**
 * Shows error on form element
 */
function showFormElementValidation($input: HTMLInputElement|HTMLSelectElement, fieldError: string | null): void {
    const $control = $input.closest(CONTROL_SELECTOR);
    const $message = $control?.querySelector(MESSAGE_SELECTOR)!;

    if ($control == null || $message == null) {
        console.error(`Validation failed for element "${$input.name}", Error: ${fieldError}.`);
        return;
    }

    const $errorId = $input.name + 'Error';

    $message.id = $errorId;
    $message.textContent = fieldError ?? 'Validation failed for this field';
    setAttribute($input, 'aria-describedby', $errorId);
    setAttribute($control, 'data-invalid', true);
}

/**
 * Hides error on form element
 */
function hideFormElementValidation($input: HTMLInputElement|HTMLSelectElement) {
    const $control = $input.closest(CONTROL_SELECTOR);
    const $message = $control?.querySelector(MESSAGE_SELECTOR)!;

    if ($control == null) {
        console.error(`No field found for element "${$input.name}".`);
        return;
    }

    $message.removeAttribute('id');
    $message.textContent = null;
    $input.removeAttribute('aria-describedby');
    $control.removeAttribute('data-invalid');
}


export interface FetchForm<TReq> {
    send(body: TReq): Promise<Response>;
    submit(validate?: boolean): Promise<void>;
    validate(): boolean;
    reset(): void;
}


export interface FetchOptions<TRes extends object, TReq extends object> {
    url?: string;
    method?: string;
    bind?: boolean;
    validations?: Validations;
    submit?(req: TReq): void;
    success?(res: TRes): string | void;
    error?(res: TRes | null, ex: Error | null): string | void;
    validate?(formData: Record<string, string>): void;
}
export interface FetchMultipartOptions<TRes extends object> extends FetchOptions<TRes, FormData> {
    multipart: true;
}

export type FetchFormOptions<TRes extends object, TReq extends object> =
    FetchMultipartOptions<TRes> |
    (FetchOptions<TRes, TReq> & { multipart?: false });


export function createForm<TRes extends object>($form: HTMLFormElement, opts: FetchFormOptions<TRes, FormData>): FetchForm<unknown>;
export function createForm<TRes extends object, TReq extends object>($form: HTMLFormElement, opts: FetchFormOptions<TRes, TReq>): FetchForm<TReq>
export function createForm<TRes extends object, TReq extends object>($form: HTMLFormElement, opts: FetchFormOptions<TRes, TReq>): FetchForm<TReq> {
    const { validations } = opts;
    const bind = opts.bind ?? true;
    const url = opts.url ?? $form.action;
    const method = opts.method ?? $form.method;
    const onValidate = opts.validate;
    const onSuccess = opts.success;
    const onError = opts.error;
    const onSubmit = opts.submit;
    const multipart = opts.multipart ?? $form.enctype === MULTIPART_MIME_TYPE;

    // Shows success message on form
    function showSuccess(data: TRes): void {
        const $error = $form.querySelector(FORM_ERROR_SELECTOR);
        const $success = $form.querySelector(FORM_SUCCESS_SELECTOR);
        const successMessage = onSuccess?.(data);

        $error?.classList.add('hidden');

        // Show success message
        if ($success) {
            const hasText = $success.textContent?.trim()?.length;

            if (successMessage != null) {
                $success.textContent = successMessage;
            } else if (!hasText) {
                // No text, dont show success message
                return;
            }

            $success.classList.remove('hidden');
        }

        $form.reset();
    }

    // Shows error message on form
    function showError(data: TRes | null, ex: Error | null): void {
        const $error = $form.querySelector(FORM_ERROR_SELECTOR);
        const $success = $form.querySelector(FORM_SUCCESS_SELECTOR);
        const errorMessage = onError?.(data, ex);

        $success?.classList.add('hidden');

        // Show error message
        if ($error != null) {
            if (ex != null) {
                console.error(ex);
            }

            $error.classList.remove('hidden');
            $error.textContent = errorMessage ?? 'There was an error submitting the form!';
        }
    }

    /**
     * Validate a form using specified validations
     */
    function validateForm(): boolean {
        const data = formData($form);
        let success = true;

        onValidate?.(data);

        if (validations == null)
            return true;

        Object.keys(validations).forEach(key => {
            // No validation for this element
            if (data[key] == null) {
                return;
            }

            let fieldError: string | null | (typeof VALIDATION_OK) = null;

            try {
                const validator = validations[key];
                fieldError = validateControl(data, key, validator);
            } catch (ex) {
                fieldError = 'Validation error in field!';
            }

            const $formElement = $form.elements[key] as HTMLInputElement|HTMLSelectElement;

            if (fieldError != VALIDATION_OK) {
                success = false;

                // Show error for form control
                showFormElementValidation($formElement, fieldError);
            } else {
                // Hide error for form control
                hideFormElementValidation($formElement);
            }
        });

        return success;
    }

    function send(data: TReq | FormData): Promise<Response> {
        let body: BodyInit;
        const headers = new Headers({
            'Accept': JSON_MIME_TYPE,
        });

        if (data instanceof FormData) {
            body = data;
        } else {
            body = JSON.stringify(data);
            headers.set('Content-Type', JSON_MIME_TYPE);
        }

        const req = fetch(url, {
            method,
            headers,
            body,
        });

        // Wait at least 2 seconds for request to complete
        return thenWait(2000, req);
    }

    async function submit(validate: boolean): Promise<void> {
        if (validate && !validateForm()) {
            const error = new Error('Validations failed!');

            showError(null, error);
            throw error;
        }

        const $error = $form.querySelector(FORM_ERROR_SELECTOR);
        const $success = $form.querySelector(FORM_SUCCESS_SELECTOR);

        // initially hide status texts
        $error?.classList.add('hidden');
        $success?.classList.add('hidden');

        try {
            const req = multipart
                ? new FormData($form)
                : formData<TReq>($form);

            onSubmit?.(req as TReq & FormData);

            const res = await send(req);
            let data: any;

            // Avoid parsing on 204 No content
            if (res.status != 204) {
                data = await res.json();
            }

            if (res.ok) {
                showSuccess(data);
            } else {
                showError(data, new Error(`${res.status} - ${res.statusText}`));
            }
        } catch (ex) {
            const err = ex instanceof TypeError ? ex : null;

            showError(null, err);
        }
    }

    // Bind validation events to each input
    if (bind) {
        const $inputs = Array.from($form.elements) as (HTMLInputElement | HTMLSelectElement)[];

        $inputs
            .filter($input => $input.closest(CONTROL_SELECTOR) != null)
            .forEach($input => {
                const tagName = $input.tagName?.toLowerCase() ?? '';
                const type = $input.type?.toLowerCase() ?? '';
                const eventName = tagName == 'input' && (type == 'radio' || type == 'checkbox')
                    ? 'change'
                    : 'input';

                $input.addEventListener(eventName, () => hideFormElementValidation($input), false);
            });
    }

    // Hook into the form submit event
    $form.addEventListener('submit', (e) => {
        e.preventDefault();

        // Reset validation before running submit handler
        const $inputs = Array.from($form.elements) as (HTMLInputElement | HTMLSelectElement)[];
        $inputs
            .filter($input => $input.closest(CONTROL_SELECTOR) != null)
            .forEach($input => hideFormElementValidation($input));

        // If custom submit handler was provided run this beforehand
        submit(true);
    }, false);

    return {
        send,
        submit,
        validate() {
            return !!validateForm();
        },
        reset() {
            $form.reset();
        },
    };
}
